import { useParams, useSearchParams } from "react-router";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import { UserCommunicationChannelType } from "@shared/schemas/user-communication-channel.schemas";
import { UserIdPublic } from "@shared/schemas/branded-id.schemas";
import type {
  EmailUserCommunicationChannel,
  WhatsAppUserCommunicationChannel,
  LinkedInUserCommunicationChannel,
} from "@shared/schemas/user-communication-channel.schemas";
import {
  useEventRouteContext,
  type EventRouteContext,
} from "@/shared/presentation/hooks/useEventRouteContext";

type AccountTabType = "settings" | "events";

function isValidTab(value: string): value is AccountTabType {
  return value === "settings" || value === "events";
}

function throwNotFound(): never {
  throw new Response(null, { status: 404 });
}

interface IsolationQueryLike {
  readonly isLoading: boolean;
  readonly data?: { readonly isIsolated: boolean } | undefined;
}

type AccessDecision =
  | { readonly status: "resolving" }
  | { readonly status: "blocked" }
  | { readonly status: "allowed" };

function resolveAccess(
  eventContext: EventRouteContext | null,
  isolationQuery: IsolationQueryLike,
): AccessDecision {
  if (eventContext !== null) {
    const { currentParticipant } = eventContext;
    if (currentParticipant.isLoading) return { status: "resolving" };
    if (currentParticipant.isolated === true) return { status: "blocked" };
    if (currentParticipant.isolated !== null) return { status: "allowed" };
  }
  if (isolationQuery.isLoading) return { status: "resolving" };
  if (isolationQuery.data?.isIsolated === true) return { status: "blocked" };
  return { status: "allowed" };
}

interface UserAccountControllerReturn {
  readonly state: {
    readonly activeTab: AccountTabType;
    readonly emailChannel: EmailUserCommunicationChannel | null;
    readonly whatsappChannel: WhatsAppUserCommunicationChannel | null;
    readonly linkedInChannel: LinkedInUserCommunicationChannel | null;
    readonly userIdPublic: string | null;
    readonly isUnauthorized: boolean;
    readonly isAccessResolving: boolean;
  };
  readonly actions: {
    readonly setActiveTab: (tab: AccountTabType) => void;
    readonly refresh: () => void;
  };
}

export function useUserAccountController(): UserAccountControllerReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userIdPublic: routeUserIdPublic } = useParams<{
    userIdPublic: string;
  }>();
  const {
    user: currentUser,
    refresh,
    useGetIsolationStatus,
  } = useUserActions();
  const eventContext = useEventRouteContext();

  const needsGlobalIsolationCheck =
    eventContext === null ||
    (eventContext.currentParticipant.isolated === null &&
      !eventContext.currentParticipant.isLoading);
  const isolationQuery = useGetIsolationStatus({
    enabled: needsGlobalIsolationCheck,
  });

  const isUnauthorized =
    currentUser !== undefined &&
    routeUserIdPublic !== UserIdPublic.unwrap(currentUser.idPublic);

  // Intentionally fail-open: if the isolation query errors, the user sees the page.
  const access: AccessDecision = isUnauthorized
    ? { status: "allowed" }
    : resolveAccess(eventContext, isolationQuery);
  if (access.status === "blocked") {
    throwNotFound();
  }
  const isAccessResolving = access.status === "resolving";

  const handleRefresh = (): void => {
    void refresh();
  };

  const tabParam = searchParams.get("tab") ?? "settings";
  const activeTab: AccountTabType = isValidTab(tabParam)
    ? tabParam
    : "settings";

  const setActiveTab = (tab: AccountTabType): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "settings") {
          next.delete("tab");
        } else {
          next.set("tab", tab);
        }
        return next;
      },
      { replace: true },
    );
  };

  const channels = currentUser?.communicationChannels ?? [];

  const emailChannel =
    channels.find(
      (ch): ch is EmailUserCommunicationChannel =>
        ch.channel === UserCommunicationChannelType.EMAIL,
    ) ?? null;

  const whatsappChannel =
    channels.find(
      (ch): ch is WhatsAppUserCommunicationChannel =>
        ch.channel === UserCommunicationChannelType.WHATSAPP,
    ) ?? null;

  const linkedInChannel =
    channels.find(
      (ch): ch is LinkedInUserCommunicationChannel =>
        ch.channel === UserCommunicationChannelType.LINKEDIN,
    ) ?? null;

  const userIdPublic =
    currentUser?.idPublic == null
      ? null
      : UserIdPublic.unwrap(currentUser.idPublic);

  return {
    state: {
      activeTab,
      emailChannel,
      whatsappChannel,
      linkedInChannel,
      userIdPublic,
      isUnauthorized,
      isAccessResolving,
    },
    actions: {
      setActiveTab,
      refresh: handleRefresh,
    },
  };
}
